import { motion } from "framer-motion";
import { Check, Phone } from "lucide-react";
import { PHONE, PHONE_HREF } from "./site";
import { EstimateDialog } from "./EstimateDialog";

const TIERS = [
  {
    id: "starter",
    name: "Starter Sparkle",
    tagline: "Weekly / Bi-Weekly upkeep",
    price: "$140–$180",
    per: "per visit",
    features: ["2 bed / 1 bath maintenance clean", "Dust, vacuum, mop, bathrooms", "Kitchen wipe, trash & tidy"],
    featured: false,
  },
  {
    id: "combo",
    name: "Honey-Do Combo",
    tagline: "Clean + Handyman in one day",
    price: "$320–$480",
    per: "combo day",
    features: ["Cleaning + handyman in one visit", "Garage org, junk removal + minor fixes", "TV mount / shelving / plumbing / drywall"],
    featured: true,
  },
  {
    id: "deep",
    name: "Deep & Detailed",
    tagline: "Sparkling every time",
    price: "$280–$350",
    per: "per visit",
    features: ["Top to bottom deep clean", "Baseboards, tile & grout, interior windows / doors", "Inside appliance cleaning"],
    featured: false,
  },
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="relative bg-[#F5EBE6]/60 pt-16 lg:pt-20 pb-20 lg:pb-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl mb-16"
        >
          <span className="font-jet text-[11px] uppercase tracking-[0.3em] text-[#993B19]">Quick Quote</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#0F172A]">
            Honest Prices. <em className="italic text-[#D96B43]">No Surprises.</em>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#475569]">
            Free same-day estimates · Licensed &amp; insured · One local team for cleaning + handyman
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.id}
              data-testid={`pricing-card-${t.id}`}
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 }}
              className={`relative flex flex-col rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2 ${
                t.featured
                  ? "bg-[#0F172A] text-[#FDFBF7] shadow-2xl shadow-[#0F172A]/25 md:-my-4 md:py-12"
                  : "bg-white border border-[#D96B43]/15 hover:border-[#D96B43]/50 hover:shadow-xl"
              }`}
            >
              {t.featured && (
                <span className="absolute -top-3 left-8 font-jet text-[10px] uppercase tracking-[0.2em] bg-[#D96B43] text-white px-4 py-1.5 rounded-full">
                  Most Loved
                </span>
              )}
              <h3 className={`font-display text-2xl tracking-tight ${t.featured ? "text-[#FDFBF7]" : "text-[#0F172A]"}`}>{t.name}</h3>
              <p className={`mt-1 font-jet text-[10px] uppercase tracking-[0.2em] ${t.featured ? "text-[#D96B43]" : "text-[#993B19]"}`}>{t.tagline}</p>

              <div className="mt-7 flex items-baseline gap-2">
                <span className={`font-display text-4xl lg:text-5xl tracking-tight ${t.featured ? "text-[#FDFBF7]" : "text-[#0F172A]"}`} data-testid={`pricing-price-${t.id}`}>
                  {t.price}
                </span>
                <span className={`font-jet text-[10px] uppercase tracking-[0.15em] ${t.featured ? "text-[#94A3B8]" : "text-[#64748B]"}`}>{t.per}</span>
              </div>

              <ul className="mt-7 space-y-3.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className={`flex gap-3 text-sm ${t.featured ? "text-[#CBD5E1]" : "text-[#475569]"}`}>
                    <Check size={16} className="mt-0.5 shrink-0 text-[#D96B43]" strokeWidth={2.5} />
                    {f}
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <EstimateDialog
                  testId={`pricing-quote-button-${t.id}`}
                  label={`Get ${t.name.split(" ")[0]} Quote`}
                  service={t.name}
                  className={`w-full py-3.5 rounded-full font-jet text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                    t.featured
                      ? "bg-[#D96B43] text-white hover:bg-[#FDFBF7] hover:text-[#0F172A]"
                      : "bg-[#0F172A] text-[#FDFBF7] hover:bg-[#D96B43]"
                  }`}
                />
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.3 }}
          className="mt-12 flex flex-wrap items-center gap-2 font-jet text-[11px] uppercase tracking-[0.15em] text-[#64748B]"
        >
          * Final price based on size, condition &amp; Treasure Coast location · Same-day estimates ·
          <a data-testid="pricing-phone-link" href={PHONE_HREF} className="inline-flex items-center gap-1.5 text-[#D96B43] hover:text-[#0F172A] transition-colors duration-300">
            <Phone size={12} /> {PHONE}
          </a>
        </motion.p>
      </div>
    </section>
  );
}
